import type { ReactNode } from "react";

export function SectionHead({
  index,
  kicker,
  title,
  desc,
  tone = "primary",
  right,
}: {
  index: string;
  kicker: string;
  title: string;
  desc?: string;
  tone?: "primary" | "secondary" | "accent";
  right?: ReactNode;
}) {
  const toneChip =
    tone === "accent"
      ? "bg-accent-100 text-accent-900 border-accent-200"
      : tone === "secondary"
        ? "bg-secondary-100 text-secondary-900 border-secondary-200"
        : "bg-primary-100 text-primary-900 border-primary-200";
  const toneBar = tone === "accent" ? "bg-accent-500" : tone === "secondary" ? "bg-secondary-500" : "bg-primary-500";
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 text-xs font-medium tracking-widest">
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 font-heading text-foreground-700">
            <span className={toneBar}></span>
            {kicker}
          </span>
          <span className="hidden h-px flex-1 bg-foreground-200 sm:block"></span>
        </div>
        <h2 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          <span className="mr-2 text-base font-bold text-foreground-400 align-top">{index}</span>
          {title}
        </h2>
        {desc && <p className="mt-2 text-sm leading-relaxed text-foreground-600">{desc}</p>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
      <div className={`h-1 w-12 rounded-full ${toneBar}`}></div>
    </div>
  );
}

export function TrendIcon({ trend }: { trend: "up" | "down" | "flat" }) {
  if (trend === "flat") {
    return (
      <span className="inline-flex h-5 w-5 items-center justify-center text-foreground-300">
        <i className="ri-subtract-line text-sm"></i>
      </span>
    );
  }
  const up = trend === "up";
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${
        up ? "bg-primary-100 text-primary-600" : "bg-secondary-100 text-secondary-700"
      }`}
    >
      <i className={`${up ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-xs`}></i>
    </span>
  );
}

export function Chip({ children, tone = "secondary" }: { children: ReactNode; tone?: "primary" | "secondary" | "accent" | "plain" }) {
  const cls =
    tone === "primary"
      ? "bg-primary-100 text-primary-800 border-primary-200"
      : tone === "accent"
        ? "bg-accent-100 text-accent-900 border-accent-200"
        : tone === "plain"
          ? "bg-background-100 text-foreground-600 border-background-200"
          : "bg-secondary-100 text-secondary-900 border-secondary-200";
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs ${cls}`}>
      {children}
    </span>
  );
}